import {api, RequestParam} from "@/shared/api";

import {BasicDataTable} from "@/shared/ui/basicDataTable";
import {
	CalendarEvent,
	CalendarEventSource,
	FilterParamSource,
	WorkingTimeRange,
	WorkingTimeRequestsBase,
	WorkingTimeRequestsBaseConfig
} from "./workingTimeRequestsBase";
import {WorkingTimeRequestDialog} from "@/features/requests/workingTimeRequestDialog";
import {WorkingTimeNoticeDialogBridge} from "@/features/requests/workingTimeNoticeDialog/workingTimeNoticeDialogBridge";
import {noticeRequestDuplicateSource} from "@/features/requests/workingTimeNoticeDialog/workingTimeNoticeDialog";
import {WorkingTimeDateSpanDialog} from "@/features/workingtime/workingTimeDateSpanDialog";
import {GroupDropdown} from "@/shared/ui/groupDropdown";
import {Combobox} from "@/shared/ui/combobox";
import {WorkingTimeRequestUtil} from "@/shared/workingtime/workingTimeRequestUtil";
import {MenuEntryList} from "@/shared/ui/contextMenu";
import {WorkingTimeDialog} from "@/features/workingtime/workingTimeDialog";
import * as TimeVacationAccountInfo from "@/features/timeaccounts/timeVacationAccountInfo";
import {statusElementsHolder} from "@/shared/recording/statusElementsHolder";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {SecurityStatus} from "@/shared/securityStatus";
import {
	Absence,
	Group,
	HolidayAbsence,
	AbstractWorkingTimeRequestResponseModel,
	TimeAccountVacationData,
	User,
	WorkingTimeDateSpan,
	WorkingTimeDateSpanCalendarEntry,
	WorkingTimeNotice,
	WorkingTimeType
} from "@/shared/timr";
import {
	formatDateRange,
	formatDateTimeRange,
	formatHalfDay,
	getCurrentISOOffset,
	getISODate,
	isSameDay,
	momentToPlainDate,
	parseISODate,
	parseISOZonedDateTime,
	Temporal
} from "@/shared/utils/datefunctions";
import {
	DurationUnit,
	ShowCalendarOptions,
	WorkingTimeRequestEvent,
	WorkingTimeRequestStatus
} from "@/shared/timrTypes";
import {isRecordingModeUserAllowed} from "@/shared/securityHelper";
import {FullCalendarSelection} from "@/shared/fullCalendarSelectionContextMenu";

export interface WorkingTimeRequestsUserConfig extends WorkingTimeRequestsBaseConfig {
	isGroupLeaderForThemself: boolean;
	absencesOfOtherUsersEnabled: boolean;
	absencesOfTeammatesEnabled: boolean;
	workingTimeTypesForNotices: WorkingTimeType[];
}

interface AbsenceTimeRange {
	userId: number;
	userFullname: string;
	startDate: string;
	endDate: string;
	startHalfDay: boolean;
	endHalfDay: boolean;
	pending: boolean;
	absences: Absence[];
	substituteFullname?: string;
	editUnit: DurationUnit;
	start?: string;
	end?: string;
}

/** Calendar request events carry the server-assigned `workingTimeRequestId`; date-spans do not. */
function isWorkingTimeRequestEvent(data: unknown): data is AbstractWorkingTimeRequestResponseModel {
	return typeof data === "object" && data !== null && "workingTimeRequestId" in data;
}

export class WorkingTimeRequestsUserPage extends WorkingTimeRequestsBase {

	private static EVENT_CLASS_ABSENCE = "fc-event-absence";
	private static GROUP_WORKING_TIME_REQUEST = "workingTimeRequestGroup";

	private workingTimeRequestDialog: WorkingTimeRequestDialog;
	private workingTimeDateSpanDialog: WorkingTimeDateSpanDialog;
	private showAbsencesOfOtherUsers = BrowserStorageSettings.getVacationAbsenceRequestCalendarView(WorkingTimeRequestsUserPage.GROUP_WORKING_TIME_REQUEST) ?? ShowCalendarOptions.PERSONAL;
	private groupDropdown: GroupDropdown;
	private userCombobox: Combobox<User>;

	private pendingAbsencesOfOtherUsersXhr?: JQueryXHR;
	private pendingSubstitutionsXhr?: JQueryXHR;

	constructor(public config: WorkingTimeRequestsUserConfig) {
		super(
			"/timr/requests/findMyWorkingTimeRequestsFromNowOrRequested.json",
			"/timr/requests/findMyWorkingTimeRequests.json",
			"/timr/requests/findMyWorkingTimesWithoutDateSpan.json",
			"/timr/workingTimeDateSpans/findMyWorkingTimeDateSpansWithoutRequest.json",
			"/timr/requests/findMyHolidays.json",
			false,
			false,
			config,
			"asc"
		);
		this.init();
		this.initFilter();
	}

	protected init() {
		super.init();

		this.initWorkingTimeRequestDialog();
		this.initWorkingTimeDateSpanDialog();
		this.reloadCurrentTimeAccountData();
		this.toggleShowAbsencesOfOtherUsers(this.showAbsencesOfOtherUsers);
		this.buildDropdownFilterMenu();

		this.workingTimeRequestCalendar.fullCalendar("option", "eventClick", (event: CalendarEvent, jsEvent: JQuery.TriggeredEvent) => {
			const element = $(jsEvent.currentTarget);
			if (element.hasClass("disabled") || element.hasClass("fc-event-disabled")) {
				return;
			}
			if (event.className.indexOf(WorkingTimeRequestsBase.EVENT_CLASS_NOTICE) !== -1) {
				this.workingTimeNoticeDialog.openForEdit(event.data as WorkingTimeNotice);
			} else if (isWorkingTimeRequestEvent(event.data)) {
				this.showOrEditRequest(event.data);
			} else {
				// The only other clickable (non-disabled) calendar event is a date-span.
				this.showOrEditDateSpan(event.data as WorkingTimeDateSpanCalendarEntry);
			}
			jsEvent.stopPropagation();
		});

		$(this.workingTimeRequestTable).on(BasicDataTable.EVENT_DBLCLICK, (e, workingTimeRequest: AbstractWorkingTimeRequestResponseModel) => {
			this.showOrEditRequest(workingTimeRequest);
		});
	}

	private filterUsersCombobox(userCombobox: Combobox<User>, selectedGroupId: number) {
		api("absences/filterUsers.json")
			.get({
				groupId: selectedGroupId
			})
			.done(data => {
				userCombobox.updateItems(data.users);
			});
	}

	protected getCustomCalendarConfig(): FullCalendar.Options {
		const calendarDate = BrowserStorageSettings.getVacationAbsenceCalendarDate(WorkingTimeRequestsUserPage.GROUP_WORKING_TIME_REQUEST);

		return {
			defaultDate: getISODate(calendarDate ?? Temporal.Now.plainDateISO()),
			header: {
				left: "title",
				center: "",
				right: "prevYear,prev,today,next,nextYear"
			}
		};
	}

	protected onCalendarViewRender(view: FullCalendar.ViewObject, element: JQuery) {
		super.onCalendarViewRender(view, element);
		BrowserStorageSettings.setVacationAbsenceCalendarDate(WorkingTimeRequestsUserPage.GROUP_WORKING_TIME_REQUEST, momentToPlainDate(view.intervalStart));
	}

	private toggleShowAbsencesOfOtherUsers(show: ShowCalendarOptions) {
		if (this.showAbsencesOfOtherUsers !== show) {
			this.showAbsencesOfOtherUsers = show;
			BrowserStorageSettings.setVacationAbsenceRequestCalendarView(WorkingTimeRequestsUserPage.GROUP_WORKING_TIME_REQUEST, show);
			this.updateShowAbsencesOfOtherUsersButtons();
			this.reloadCalendar();
		}
	}

	protected updateCalendarToolbar(intervalStart: Temporal.PlainDate, intervalEnd: Temporal.PlainDate) {
		super.updateCalendarToolbar(intervalStart, intervalEnd);
		this.updateShowAbsencesOfOtherUsersButtons();
	}

	private buildDropdownFilterMenu() {
		if (this.config.absencesOfOtherUsersEnabled || this.config.absencesOfTeammatesEnabled) {
			const toolbar = $(".working-time-requests-calendar").find(".fc-toolbar");
			toolbar.append(`<div id='fctoolbarBottom' class='text-left fc-toolbar-bottom display-flex justify-left'><div class="fc-left"></div><div class='fc-right'></div></div>`);
			const toolbarBottom = toolbar.find("#fctoolbarBottom");
			toolbarBottom.find(".fc-left").append(`<div id="filterButtons" class="fc-button-group display-flex"></div>`);

			toolbarBottom.find("#filterButtons").append(`<button type="button" class="fc-showPersonal-button fc-button fc-state-default fc-corner-left selected">` + messages["requests.workingTimeRequestsUser.showAbsencesOfOtherUsers.personal"] + `</button>`);
			if (this.config.absencesOfTeammatesEnabled) {
				toolbarBottom.find("#filterButtons").append(`<button type="button" class="fc-showGroup-button fc-button fc-state-default">` + messages["requests.workingTimeRequestsUser.showAbsencesOfOtherUsers.group"] + `</button>`);
			}
			if (!this.config.absencesOfOtherUsersEnabled) {
				toolbarBottom.find(".fc-showGroup-button").addClass("fc-corner-right");
			} else {
				toolbarBottom.find("#filterButtons").append(`<button type="button" class="fc-showAll-button fc-button fc-state-default fc-corner-right">` +
					messages["requests.workingTimeRequestsUser.showAbsencesOfOtherUsers.all"] + `</button>`);
			}

			toolbarBottom.find(".fc-right").append(`<div id='fullcalendarFilterView' class='display-flex fc-filter-groups'>
					${SecurityStatus.isCorporateFeaturesAllowed() ? this.buildGroupDropdown() : ""}
					${this.buildUserDropdown()}
				</div>`);

			toolbar.find(".fc-showAll-button").click(() => this.toggleShowAbsencesOfOtherUsers(ShowCalendarOptions.ALL));
			toolbar.find(".fc-showGroup-button").click(() => this.toggleShowAbsencesOfOtherUsers(ShowCalendarOptions.GROUP));
			toolbar.find(".fc-showPersonal-button").click(() => this.toggleShowAbsencesOfOtherUsers(ShowCalendarOptions.PERSONAL));
		}
		this.updateShowAbsencesOfOtherUsersButtons();
	}

	private buildGroupDropdown() {
		return `
			<div class='form-group display-flex'>
				<label for='fullcalendarFilterGroupQueryDropdown' class='control-label text-right' style='margin-top: auto; margin-bottom:auto'>
					${messages["fullcalendar.filter.group"]}
				</label>
				<div class='dropdown'>
					<div class='input-group' id='fullcalendarFilterGroupQueryDropdown'>
						<input type='hidden' class='tree-dropdown-id' name='group'/>
						<div>
							<div class='tree-dropdown-input'>
								<input type='text' id='fullcalendarFilterGroupQuery' class='form-control tree-dropdown-query' autocomplete='off'>
								<div class='tree-dropdown-description form-control'></div>
							</div>
							<div class='dropdown-menu dropdown-content'>
								<div class='tree-default tree-dropdown-tree'></div>
							</div>
						</div>
						<span class='input-group-btn'>
							<button class='btn btn-default btn-icon tree-dropdown-caret' type='button' tabindex='-1'>
								<i class='fas fa-caret-down'></i>
							</button>
						</span>
					</div>
				</div>
			</div>`;
	}

	private buildUserDropdown() {
		return `
			<div class='display-flex'>
				<label class='control-label text-right' for='fullcalendarFilterUserFilter' style='margin-top: auto; margin-bottom:auto'>
					${messages["fullcalendar.filter.user"]}
				</label>
				<div class='input-group'>
					<input name='id' type='hidden' id='fullcalendarFilterUserId'/>
					<input type='text' id='fullcalendarFilterUserFilter' class='form-control'/>
					<span class='input-group-btn'>
						<button type='button' id='fullcalendarFilterUserCaret' class='btn btn-default btn-icon'>
							<i class='fas fa-caret-down'></i>
						</button>
					</span>
				</div>
			</div>`;
	}

	private updateShowAbsencesOfOtherUsersButtons() {
		const toolbar = $(".working-time-requests-calendar").find(".fc-toolbar");
		toolbar.find(".fc-showAll-button").toggleClass("selected", this.showAbsencesOfOtherUsers === ShowCalendarOptions.ALL);
		toolbar.find(".fc-showGroup-button").toggleClass("selected", this.showAbsencesOfOtherUsers === ShowCalendarOptions.GROUP);
		toolbar.find(".fc-showPersonal-button").toggleClass("selected", this.showAbsencesOfOtherUsers === ShowCalendarOptions.PERSONAL);
		this.updateShowFilterDropdowns(toolbar);
	}

	private updateShowFilterDropdowns(toolbar: JQuery) {
		if (this.showAbsencesOfOtherUsers !== ShowCalendarOptions.ALL) {
			toolbar.find("#fullcalendarFilterView").hide();
		} else {
			toolbar.find("#fullcalendarFilterView").show();
		}
	}

	protected buildCalendarEventSources(): CalendarEventSource[] {
		const eventSources = super.buildCalendarEventSources();
		eventSources.push({
			events: (start, end, _timezone, callback) => {
				this.loadAbsencesOfOtherUsers(momentToPlainDate(start), momentToPlainDate(end), callback);
			}
		});
		if (SecurityStatus.isSubstitutesAllowed()) {
			eventSources.push({
				events: (start, end, _timezone, callback) => {
					this.loadSubstitutions(momentToPlainDate(start), momentToPlainDate(end), callback);
				}
			});
		}
		if (SecurityStatus.isWorkingTimeNoticesAllowed()) {
			// Own notices are always shown (mirroring the always-on own absence sources from the base class);
			// other users' notices are layered on top per scope.
			eventSources.push({
				events: (start, end, _timezone, callback) => {
					this.loadMyCalendarNotices(momentToPlainDate(start), momentToPlainDate(end), callback);
				}
			});
			eventSources.push({
				events: (start, end, _timezone, callback) => {
					this.loadNoticesOfOtherUsers(momentToPlainDate(start), momentToPlainDate(end), callback);
				}
			});
		}
		return eventSources;
	}

	protected initWorkingTimeRequestDialog() {
		this.workingTimeRequestDialog = new WorkingTimeRequestDialog({
			dialogId: "#workingTimeRequestDialog",
			workingTimeTypes: this.config.workingTimeTypes,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			customFieldDefinitions: this.config.customFieldDefinitions
		});
		$(this.workingTimeRequestDialog).on(WorkingTimeRequestDialog.EVENT_REQUEST_DELETED, () => {
			this.reload();
		}).on(WorkingTimeRequestDialog.EVENT_REQUEST_EDITED, () => {
			this.reload();
		}).on(WorkingTimeRequestDialog.EVENT_REQUEST_STATUS_CHANGED, () => {
			this.reload();
		});
	}

	protected override initWorkingTimeNoticeDialog() {
		this.workingTimeNoticeDialog = new WorkingTimeNoticeDialogBridge({
			workingTimeTypes: this.config.workingTimeTypesForNotices,
			showUserRow: false, // Dialog is only used for own notices on this page, so user row with selector is not needed
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself
		});
		$(this.workingTimeNoticeDialog).on(WorkingTimeNoticeDialogBridge.EVENT_NOTICE_SAVED, () => {
			this.reload();
		}).on(WorkingTimeNoticeDialogBridge.EVENT_NOTICE_DELETED, () => {
			this.reload();
		});
	}

	protected initWorkingTimeDateSpanDialog() {
		this.workingTimeDateSpanDialog = new WorkingTimeDateSpanDialog({
			dialogId: "#workingTimeDateSpanDialog",
			workingTimeTypes: this.config.workingTimeTypes
		});
		$(this.workingTimeDateSpanDialog).on(WorkingTimeDateSpanDialog.EVENT_DATE_SPAN_DELETED, () => {
			this.reload();
		}).on(WorkingTimeDateSpanDialog.EVENT_DATE_SPAN_EDITED, () => {
			this.reload();
		});
	}

	private showOrEditRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		// WTR cannot be edited if InApproval
		if (workingTimeRequest.status === WorkingTimeRequestStatus.ApprovalRequested) {
			this.editWorkingTimeRequest(workingTimeRequest);
		} else {
			this.showWorkingTimeRequest(workingTimeRequest);
		}
	}

	private showOrEditDateSpan(workingTimeDateSpan: WorkingTimeDateSpanCalendarEntry) {
		if ((globals.isGroupLeader || isRecordingModeUserAllowed(workingTimeDateSpan.workingTimeType.recordingModeUser)) &&
			workingTimeDateSpan.editable) {
			this.editWorkingTimeDateSpan(workingTimeDateSpan);
		} else {
			this.showWorkingTimeDateSpan(workingTimeDateSpan);
		}
	}

	protected initFilter() {
		super.initFilter();
		this.buildDropdownFilterMenu();

		$("#workingTimeType").change(() => {
			this.reload();
		});

		$(".addAbsenceButton").click(() => {
			this.addAbsence();
		}).toggleClass("disabled", this.getWorkingTimeTypesForAddAbsence().length === 0);

		// `addNoticeButton` is server-side gated on hasWorkingTimeTypesForNotices in the JSP,
		// so it only renders when at least one working time type is selectable.
		$("#addNoticeButton").click(() => {
			this.workingTimeNoticeDialog?.openForCreate();
		});

		// Dropdown menus for groups and users
		this.userCombobox = new Combobox<User>({
			items: [],
			formatter: (user) => {
				return user.fullname;
			},
			idElement: $("#fullcalendarFilterUserId"),
			filterElement: $("#fullcalendarFilterUserFilter"),
			caretElement: $("#fullcalendarFilterUserCaret"),
			emptyMessage: messages["user.filter.all"],
			clearEntryMessage: messages["user.filter.all"]
		});
		$(this.userCombobox).on(Combobox.EVENT_CHANGED, () => {
			this.reloadCalendar();
		});
		this.groupDropdown = new GroupDropdown({
			rootElement: $("#fullcalendarFilterGroupQueryDropdown"),
			allowSelectionOfNotAssigned: true,
			emptyMessage: messages["group.filter.all"],
			clearEntryMessage: messages["group.filter.all"]
		}, {
			loadRootNodesURL: "requests/loadRootGroups.json",
			loadSubNodesURL: "requests/loadSubGroups.json",
			filterNodesURL: "requests/filterGroups.json"
		});
		this.filterUsersCombobox(this.userCombobox, null);
		$(this.groupDropdown).on(GroupDropdown.EVENT_SELECTED, (e, group: Group | null) => {
			// filter events after group
			this.filterUsersCombobox(this.userCombobox, group?.id);
			this.reloadCalendar();
		});
	}

	protected buildFilterParams(filterParamSource: FilterParamSource): RequestParam[] {
		return [
			...super.buildFilterParams(filterParamSource),
			{name: "workingTimeTypeId", value: $("#workingTimeType").val() as string},
			{name: "timeZone", value: getCurrentISOOffset()},
		];
	}

	protected buildCalendarTitleRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel): string {
		return workingTimeRequest.workingTimeType.name;
	}

	protected buildCalendarTitleWorkingTime(workingTimeRange: WorkingTimeRange): string {
		return workingTimeRange.workingTimeType.name;
	}

	protected buildCalendarTitleWorkingTimeDateSpan(workingTimeDateSpan: WorkingTimeDateSpan): string {
		return workingTimeDateSpan.workingTimeType.name;
	}

	protected buildCalendarTitleAbsence(absence: AbsenceTimeRange): string {
		return absence.pending ?
			absence.userFullname + " " + messages["requests.workingTimeRequestsUser.absenceRequested"] :
			absence.userFullname + " " + messages["requests.workingTimeRequestsUser.absent"];
	}

	protected buildCalendarTitleSubstitution(substitution: AbsenceTimeRange): string {
		return substitution.pending ?
			messages.withArguments("requests.workingTimeRequestsUser.substitutionRequested", substitution.userFullname) :
			messages.withArguments("requests.workingTimeRequestsUser.substitution", substitution.userFullname);
	}

	protected buildPopoverContent(event: CalendarEvent): JQuery {
		if (event.className.indexOf(WorkingTimeRequestsUserPage.EVENT_CLASS_ABSENCE) !== -1) {
			return this.buildPopoverContentAbsence(event.data as AbsenceTimeRange);
		} else if (event.className.indexOf(WorkingTimeRequestsUserPage.EVENT_CLASS_HOLIDAY) !== -1) {
			return this.buildPopoverContentHolidayAbsence(event.data as HolidayAbsence[]);
		} else if (event.className.indexOf(WorkingTimeRequestsUserPage.EVENT_CLASS_SUBSTITUTION) !== -1) {
			return this.buildPopoverContentSubstitution(event.data as AbsenceTimeRange);
		} else {
			return super.buildPopoverContent(event);
		}
	}

	protected buildPopoverContentAbsence(absence: AbsenceTimeRange): JQuery {
		const content = $("<div>");
		content.append($("<h5>").text(absence.userFullname));
		if (absence.pending) {
			content.append($("<p>").text(messages["requests.workingTimeRequestsUser.absenceRequested"]));
		} else {
			content.append($("<p>").text(messages["requests.workingTimeRequestsUser.absent"]));
		}
		if (absence.editUnit === DurationUnit.Minutes) {
			content.append($("<p>").html(formatDateTimeRange(
				parseISOZonedDateTime(absence.start),
				parseISOZonedDateTime(absence.end)
			)));
		} else {
			const startDate = parseISODate(absence.startDate);
			const endDate = parseISODate(absence.endDate);
			content.append($("<p>").html(formatDateRange(startDate, endDate, absence.startHalfDay, absence.endHalfDay)));
		}
		this.appendSubstitutePopoverContent(content, absence.substituteFullname);
		return content;
	}

	protected buildPopoverContentHolidayAbsence(holidays: HolidayAbsence[]): JQuery {
		const content = $("<div>")
			.append($("<h5>").text(messages["requests.workingTimeRequests.holiday"]));
		const list = holidays.filter((holiday, index, arr) => arr.findIndex(h => h.holidayId === holiday.holidayId) === index);
		list.forEach(holiday => {
			const data = $("<p>").text(`${holiday.holidayDescription} (${holiday.holidayCalendarDescription})`);
			if (holiday.halfDay) {
				data.append(formatHalfDay(true));
			}
			content.append(data);
		});
		return content;
	}

	protected buildPopoverContentSubstitution(substitution: AbsenceTimeRange): JQuery {
		const content = $("<div>");
		const startDate = parseISODate(substitution.startDate);
		const endDate = parseISODate(substitution.endDate);
		const header = $("<h5>");
		if (substitution.pending) {
			header.text(messages.withArguments("requests.workingTimeRequestsUser.substitutionRequested", substitution.userFullname));
		} else {
			header.text(messages.withArguments("requests.workingTimeRequestsUser.substitution", substitution.userFullname));
		}
		content.append(header);
		content.append($("<p>").html(formatDateRange(startDate, endDate, substitution.startHalfDay, substitution.endHalfDay)));
		return content;
	}

	protected initWorkingTimeDialog() {
		this.workingTimeDialog = new WorkingTimeDialog({
			dialogId: "#workingTimeDialog",
			workingTimeTypes: this.config.workingTimeTypes,
			users: [],
			userSelectionEnabled: false,
			customFieldDefinitions: this.config.customFieldDefinitions,
			defaultStatusNumber: this.config.defaultStatusNumber,
			allowedUserIds: [],
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.dateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.dateTimeChangeableByUserDayRangeMode
		});
		$(this.workingTimeDialog).on(WorkingTimeDialog.EVENT_RECORD_CREATED, () => {
			this.reload();
		});
		$(this.workingTimeDialog).on(WorkingTimeDialog.EVENT_REQUEST_CREATED, () => {
			this.reload();
		});
		$(this.workingTimeDialog).on(WorkingTimeDialog.EVENT_OPEN_REQUEST, (e, workingTimeRequest: AbstractWorkingTimeRequestResponseModel) => {
			this.workingTimeRequestDialog.edit(workingTimeRequest);
		});
	}

	protected reload() {
		super.reload();

		this.reloadCurrentTimeAccountData();
	}

	protected addAbsence(start: Temporal.ZonedDateTime | null = null, end: Temporal.ZonedDateTime | null = null) {
		super.addAbsence(start, end);

		const workingTimeTypes = this.getWorkingTimeTypesForAddAbsence();
		this.workingTimeDialog.create({
			workingTimeTypeId: null,
		}, false, workingTimeTypes);
		this.workingTimeDialog.setStartEndTime(start, end);
	}

	protected reloadCurrentTimeAccountData() {
		TimeVacationAccountInfo.toggleTimeVacationAccountInfoPanelLoadingIndicator(true);
		if (SecurityStatus.isTimeAccountAllowed()) {
			api("timeaccounts/getCurrentTimeAccountVacationData.json")
				.get({
					timeZone: getCurrentISOOffset()
				})
				.done((timeAccountData: TimeAccountVacationData) => {
					this.showTimeAccountData(timeAccountData);
					TimeVacationAccountInfo.toggleTimeVacationAccountInfoPanelLoadingIndicator(false);
				})
				.fail(() => {
					TimeVacationAccountInfo.toggleTimeVacationAccountInfoPanelLoadingIndicator(false);
				});
		}
	}

	protected createContextMenuItems(): MenuEntryList<AbstractWorkingTimeRequestResponseModel> {
		return [{
			label: messages["requests.workingTimeRequestsUser.context.show"],
			action: (workingTimeRequest) => this.showWorkingTimeRequest(workingTimeRequest)
		}, {
			label: messages["requests.workingTimeRequestsUser.context.edit"],
			action: (workingTimeRequest) => this.editWorkingTimeRequest(workingTimeRequest),
			// WTR cannot be edited if InApproval
			active: (workingTimeRequest) => workingTimeRequest.status === WorkingTimeRequestStatus.ApprovalRequested
		}, {
			label: messages["requests.workingTimeRequestsUser.context.duplicate"],
			action: (workingTimeRequest) => this.duplicateWorkingTimeRequest(workingTimeRequest),
			// A notice request's type may have been disabled or archived since
			active: (workingTimeRequest) => !WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(workingTimeRequest)
				|| this.workingTimeNoticeDialog.hasWorkingTimeType(workingTimeRequest.workingTimeType.id)
		}, {
			label: messages["requests.workingTimeRequestsUser.context.delete"],
			action: (workingTimeRequest) => this.deleteWorkingTimeRequest(workingTimeRequest),
			active: (workingTimeRequest) => WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest)
		}, "divider", {
			label: messages["requests.workingTimeRequestsUser.context.requestCancellation"],
			action: (workingTimeRequest) => this.requestCancellation(workingTimeRequest),
			active: (workingTimeRequest) => workingTimeRequest.status === WorkingTimeRequestStatus.Approved,
			show: (workingTimeRequest) => workingTimeRequest.user.id === globals.userId
		}, {
			label: messages["requests.workingTimeRequestsUser.context.cancelImmediately"],
			action: (workingTimeRequest) => this.cancelImmediately(workingTimeRequest),
			active: (workingTimeRequest) => workingTimeRequest.status === WorkingTimeRequestStatus.Approved,
			show: (workingTimeRequest) => WorkingTimeRequestUtil.isImmediateStatusChangeAllowed(workingTimeRequest, this.config.isGroupLeaderForThemself)
		}];
	}

	private editWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		this.workingTimeRequestDialog.edit(workingTimeRequest);
	}

	private showWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		this.workingTimeRequestDialog.show(workingTimeRequest);
	}

	private duplicateWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		if (WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(workingTimeRequest)) {
			this.workingTimeNoticeDialog.openForDuplicate(noticeRequestDuplicateSource(workingTimeRequest));
		} else {
			this.workingTimeRequestDialog.duplicate(workingTimeRequest);
		}
	}

	private editWorkingTimeDateSpan(workingTimeDateSpan: WorkingTimeDateSpan) {
		this.workingTimeDateSpanDialog.edit(workingTimeDateSpan);
	}


	private showWorkingTimeDateSpan(workingTimeDateSpan: WorkingTimeDateSpan) {
		this.workingTimeDateSpanDialog.show(workingTimeDateSpan);
	}

	private deleteWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		WorkingTimeRequestUtil.deleteWorkingTimeRequest(workingTimeRequest, () => this.reload());
	}

	private requestCancellation(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		WorkingTimeRequestUtil.requestCancellationWorkingTimeRequest(workingTimeRequest, () => this.reload());
	}

	private cancelImmediately(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		BootstrapModal.confirmPrompt({
			title: "workingTimeRequestStatus.cancelImmediately.title",
			message: messages["workingTimeRequestStatus.cancelImmediately.message"],
			yesLabel: "confirmation.cancelImmediately",
			yesCss: "btn-warning",
			noLabel: "confirmation.cancel",
			noCss: "btn-default",
			promptNameTag: "statusComment",
			css: {
				header: "bg-warning"
			},
			yesAction: (modal: BootstrapModal.Modal) => {
				api(WorkingTimeRequestUtil.SEND_WORKING_TIME_REQUEST_EVENT_URL)
					.post({
						workingTimeRequestId: workingTimeRequest.id,
						workingTimeRequestEvent: WorkingTimeRequestEvent.CANCEL_IMMEDIATELY,
						comment: modal.getReason()
					})
					.done(() => {
						modal.close();
						statusElementsHolder.updateRequestsApproval();
						this.reload();
					}).fail((response) => {
					WorkingTimeRequestUtil.handleConfirmPromptFail(response, modal);
				});
			}
		});
	}

	private loadAbsencesOfOtherUsers(start: Temporal.PlainDate, end: Temporal.PlainDate, callback: (events: FullCalendar.EventObject[]) => void) {
		this.pendingAbsencesOfOtherUsersXhr?.abort();
		let url: string;
		let params: RequestParam[];
		switch (this.showAbsencesOfOtherUsers) {
			case ShowCalendarOptions.ALL:
				params = this.buildFilterParams(FilterParamSource.Calendar);
				params.push({name: "from", value: getISODate(start)});
				params.push({name: "to", value: getISODate(end)});
				params.push({name: "groupId", value: this.groupDropdown.getSelectedId()});
				params.push({name: "userId", value: this.userCombobox.getSelectedItem()?.id});
				url = "requests/findAbsencesOfOtherUsers.json";
				break;
			case ShowCalendarOptions.GROUP:
				params = this.buildFilterParams(FilterParamSource.Calendar);
				params.push({name: "from", value: getISODate(start)});
				params.push({name: "to", value: getISODate(end)});
				url = "requests/findAbsencesOfTeammates.json";
				break;
			case ShowCalendarOptions.PERSONAL:
				// No absences to load here
				callback([]);
				return;
		}

		this.pendingAbsencesOfOtherUsersXhr = api(url).get(params);
		this.pendingAbsencesOfOtherUsersXhr.then(
			(response: Absence[]) => this.buildEvents(response, callback),
			() => callback([])
		);
	}

	/**
	 * Loads the current user's own notices. Always active, regardless of the selected scope, so own notices stay
	 * visible in the group/all views just like own absences (which the base class loads as a dedicated source).
	 */
	private loadMyCalendarNotices(start: Temporal.PlainDate, end: Temporal.PlainDate, callback: (events: FullCalendar.EventObject[]) => void) {
		const params = [
			{name: "from", value: getISODate(start)},
			{name: "to", value: getISODate(end)}
		];
		void api("notices/findMyCalendarEntries.json")
			.get(params)
			.done((response: WorkingTimeNotice[]) => callback(this.buildNoticeEvents(response)));
	}

	/**
	 * Loads notices of other users for the group/all scopes (these endpoints exclude the current user, whose own
	 * notices come from {@link loadMyCalendarNotices}). In the personal scope there are no other users to load.
	 */
	private loadNoticesOfOtherUsers(start: Temporal.PlainDate, end: Temporal.PlainDate, callback: (events: FullCalendar.EventObject[]) => void) {
		const params: Array<{ name: string; value: unknown }> = [
			{name: "from", value: getISODate(start)},
			{name: "to", value: getISODate(end)}
		];
		let url: string;
		switch (this.showAbsencesOfOtherUsers) {
			case ShowCalendarOptions.PERSONAL:
				// Own notices are loaded by loadMyCalendarNotices; nothing else to show here.
				callback([]);
				return;
			case ShowCalendarOptions.GROUP:
				url = "notices/findCalendarEntriesOfTeammates.json";
				break;
			case ShowCalendarOptions.ALL:
				params.push(
					{name: "groupId", value: this.groupDropdown?.getSelectedId()},
					{name: "userId", value: this.userCombobox?.getSelectedItem()?.id}
				);
				url = "notices/findCalendarEntriesOfOtherUsers.json";
				break;
		}

		void api(url)
			.get(params)
			.done((response: WorkingTimeNotice[]) => callback(this.buildNoticeEvents(response)));
	}

	private buildNoticeEvents(response: WorkingTimeNotice[]): FullCalendar.EventObject[] {
		return response.map(notice => ({
			title: this.buildCalendarTitleNotice(notice),
			start: notice.start,
			end: getISODate(parseISODate(notice.end)?.add({days: 1})),
			allDay: true,
			className: [
				WorkingTimeRequestsUserPage.EVENT_CLASS_NOTICE,
				"request-notice",
				"request-category-" + notice.workingTimeTypeCategory
			],
			data: notice
		}));
	}

	private loadSubstitutions(start: Temporal.PlainDate, end: Temporal.PlainDate, callback: (events: CalendarEvent[]) => void) {
		this.pendingSubstitutionsXhr?.abort();
		const params = this.buildFilterParams(FilterParamSource.Calendar);
		params.push({name: "from", value: getISODate(start)});
		params.push({name: "to", value: getISODate(end)});
		this.pendingSubstitutionsXhr = api("workingTimeDateSpans/findMySubstitutions.json").get(params);
		this.pendingSubstitutionsXhr.then(
			(response: Absence[]) => {
				const events: CalendarEvent[] = $.map(this.reduceAbsences(response), (absenceTimeRange: AbsenceTimeRange) => {
					return {
						title: this.buildCalendarTitleSubstitution(absenceTimeRange),
						start: absenceTimeRange.startDate,
						end: getISODate(parseISODate(absenceTimeRange.endDate)?.add({days: 1})),
						allDay: true,
						className: [
							WorkingTimeRequestsUserPage.EVENT_CLASS_SUBSTITUTION,
							"fc-event-disabled",
							"request-substitution"
						],
						data: absenceTimeRange
					};
				});
				callback(events);
			},
			() => callback([])
		);
	}

	/**
	 * Reduces a list of absences where contiguous absences are merged together to be able to display them like requests.
	 * Requires a sorted list of absences by user, workingTimeType, start, end
	 * @param absences A sorted list of absences
	 * @return Returns a list of absence time ranges where contiguous absences are merged together
	 */
	private reduceAbsences(absences: Absence[]): AbsenceTimeRange[] {
		const reducedList: AbsenceTimeRange[] = [];
		for (const absence of absences) {
			const previous = reducedList.length > 0 ? reducedList[reducedList.length - 1] : null;
			const previousEnd = parseISODate(previous?.endDate);
			const requiredNextStart = previousEnd?.add({days: 1});
			const pendingStart = parseISODate(absence.startDate);
			// Minute-based absences are never merged into a day range: they stay standalone entries so their times
			// can be shown, and they must not absorb (or be absorbed into) an adjacent day-based absence.
			const isFirst = previous == null;
			const differentUser = previous ? absence.userId !== previous.userId : false;
			const differentPending = previous ? absence.pending !== previous.pending : false;
			const nonContiguous = previous ? !isSameDay(pendingStart, requiredNextStart) : false;
			const previousEndsHalfDay = previous?.endHalfDay ?? false;
			const currentSpansMultipleDays = absence.startDate !== absence.endDate;
			const minuteBasedConflict = absence.editUnit === DurationUnit.Minutes || previous?.editUnit === DurationUnit.Minutes;

			// Start new range if
			if (isFirst || differentUser || differentPending || nonContiguous || previousEndsHalfDay || currentSpansMultipleDays || minuteBasedConflict) {
				reducedList.push({
					userId: absence.userId,
					userFullname: absence.userFullname,
					startDate: absence.startDate,
					endDate: absence.endDate,
					startHalfDay: absence.startHalfDay,
					endHalfDay: absence.endHalfDay,
					pending: absence.pending,
					absences: [absence],
					substituteFullname: absence.substituteFullname,
					editUnit: absence.editUnit,
					start: absence.start,
					end: absence.end
				});
			} else {
				previous.endDate = absence.endDate;
				previous.endHalfDay = (absence.startHalfDay || absence.endHalfDay);
				previous.absences.push(absence);
			}
		}
		return reducedList;
	}

	protected loadHolidays(start: Temporal.PlainDate, end: Temporal.PlainDate, callback: (events: CalendarEvent[]) => void) {
		this.pendingHolidaysXhr?.abort();
		let holidayUrl: "requests/findHolidaysOfOtherUsers.json" | "requests/findHolidaysOfTeammates.json" | "requests/findHolidaysForMyself.json";
		switch (this.showAbsencesOfOtherUsers) {
			case ShowCalendarOptions.ALL:
				holidayUrl = "requests/findHolidaysOfOtherUsers.json";
				break;
			case ShowCalendarOptions.GROUP:
				holidayUrl = "requests/findHolidaysOfTeammates.json";
				break;
			case ShowCalendarOptions.PERSONAL:
				holidayUrl = "requests/findHolidaysForMyself.json";
				break;
		}
		const params = this.buildFilterParams(FilterParamSource.Calendar);
		params.push({name: "from", value: getISODate(start)});
		params.push({name: "to", value: getISODate(end)});
		params.push({name: "groupId", value: this.groupDropdown.getSelectedId()});
		params.push({name: "userId", value: this.userCombobox.getSelectedItem()?.id});
		this.pendingHolidaysXhr = api(holidayUrl).get(params);
		this.pendingHolidaysXhr.then(
			(response: { data: HolidayAbsence[] }) => {
				// Group holidays by date
				const reducedHolidays = response.data.reduce((holidayMap: Record<string, HolidayAbsence[]>, holiday) => {
					(holidayMap[holiday.date] = holidayMap[holiday.date] ?? []).push(holiday);
					return holidayMap;
				}, {});
				// Create events for each date
				const events = $.map(reducedHolidays, (holidays: HolidayAbsence[]) => {
					return {
						title: this.buildCalendarTitleHolidayAbsence(holidays),
						start: holidays[0].date,
						end: getISODate(parseISODate(holidays[0].date)?.add({days: 1})),
						allDay: true,
						className: [
							WorkingTimeRequestsBase.EVENT_CLASS_HOLIDAY,
							"fc-event-disabled",
							"bg-orange-300"
						],
						data: holidays
					};
				});
				callback(events);
			},
			() => callback([])
		);
	}

	protected buildCalendarTitleHolidayAbsence(holidays: HolidayAbsence[]): string {
		return holidays.map((holiday) => holiday.holidayDescription)
			.filter((description, index, array) => array.indexOf(description) === index)
			.reduce((title, description) => {
				return title ? title + ", " + description : description;
			}, "");
	}

	private buildEvents(response: Absence[], callback: (events: FullCalendar.EventObject[]) => void) {
		const events = $.map(this.reduceAbsences(response), (absenceTimeRange: AbsenceTimeRange) => {
			return {
				title: this.buildCalendarTitleAbsence(absenceTimeRange),
				start: absenceTimeRange.startDate,
				end: getISODate(parseISODate(absenceTimeRange.endDate)?.add({days: 1})),
				allDay: true,
				className: [
					WorkingTimeRequestsUserPage.EVENT_CLASS_ABSENCE,
					"fc-event-disabled",
					"request-absence"
				],
				data: absenceTimeRange
			};
		});
		callback(events);
	}

	protected override addNotice(selection: FullCalendarSelection) {
		this.workingTimeNoticeDialog?.openForCreate({
			start: selection.start,
			end: selection.end,
		});
	}
}
